import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getClientsBySeller } from '../../Redux/Actions/actions';
import ClientForm from '../ClientForm/ClientForm';
import styles from './ClientSelector.module.css';

// Categorías (clase) soportadas hoy por ClientForm. Mantener sincronizado con
// el <select> de ClientForm.jsx.
const CATEGORIAS_CLIENTE = ['VIP', 'Premium', 'Inversor', 'Primera compra', 'Regular'];

const ClientSelector = ({
  selectedClients = [],
  onSelectionChange,
  sellerId,
  // Habilita filtro por categoría (clase) sobre el listado.
  showCategoryFilter = false,
  // Habilita botón "+ Crear cliente" con ClientForm inline. Al crear, se
  // recarga el listado y se auto-selecciona el nuevo cliente.
  allowCreate = false,
}) => {
  const dispatch = useDispatch();
  const { clientsBySeller, clientsLoading, clientsError } = useSelector(state => state);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (sellerId) {
      // Cargar solo clientes activos
      dispatch(getClientsBySeller(sellerId, { activo: 'true' }));
    }
  }, [sellerId, dispatch]);

  const handleClientToggle = (client) => {
    const isSelected = selectedClients.some(selected => selected.id === client.id);
    let newSelection;
    
    if (isSelected) {
      // Remover propietario de la selección
      newSelection = [];
    } else {
      // Solo permitir un propietario (reemplazar selección anterior)
      newSelection = [client];
    }
    
    onSelectionChange(newSelection);
  };

  const filteredClients = useMemo(() => clientsBySeller.filter(client =>
    client.activo && // Solo clientes activos
    (!categoriaFilter || client.categoria === categoriaFilter) &&
    (client.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
     client.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
     client.mail.toLowerCase().includes(searchTerm.toLowerCase()))
  ), [clientsBySeller, categoriaFilter, searchTerm]);

  // Callback al crear un cliente desde el form inline: refrescar listado y
  // seleccionar al recién creado sin recargar la página.
  const handleCreated = async (created) => {
    setIsCreating(false);
    try {
      await dispatch(getClientsBySeller(sellerId, { activo: 'true' }));
    } catch { /* no-op: el reducer ya inserta el nuevo cliente via CREATE_CLIENT_SUCCESS */ }
    if (created && created.id) {
      onSelectionChange([created]);
    }
  };

  if (!sellerId) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>Seleccionar Cliente</h3>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={styles.toggleButton}
        >
          {isExpanded ? '▼' : '▶'} 
          {selectedClients.length > 0 && (
            <span className={styles.count}>(Propietario seleccionado)</span>
          )}
        </button>
      </div>

      {isExpanded && (
        <div className={styles.content}>
          <div className={styles.searchContainer} style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Buscar propietario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              style={{ flex: '1 1 200px' }}
            />
            {showCategoryFilter && (
              <select
                value={categoriaFilter}
                onChange={(e) => setCategoriaFilter(e.target.value)}
                className={styles.searchInput}
                style={{ flex: '0 1 180px' }}
                aria-label="Filtrar por categoría"
              >
                <option value="">Todas las categorías</option>
                {CATEGORIAS_CLIENTE.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}
            {allowCreate && !isCreating && (
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className={styles.toggleButton}
                style={{ whiteSpace: 'nowrap' }}
              >
                + Crear cliente
              </button>
            )}
          </div>

          {/* ClientForm se renderiza en un portal (fuera del <form> de la propiedad)
              para evitar formularios anidados, que el navegador invalida y que
              provocaban que "Crear cliente" enviara el form externo. */}
          {allowCreate && isCreating && createPortal(
            <div
              className={styles.createOverlay}
              onClick={() => setIsCreating(false)}
            >
              <div
                className={styles.createModal}
                onClick={(e) => e.stopPropagation()}
              >
                <ClientForm
                  onSuccess={handleCreated}
                  onCancel={() => setIsCreating(false)}
                />
              </div>
            </div>,
            document.body
          )}

          {clientsLoading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Cargando clientes...</span>
            </div>
          )}

          {clientsError && (
            <div className={styles.error}>
              Error al cargar clientes: {clientsError}
            </div>
          )}

          {!clientsLoading && filteredClients.length === 0 && (
            <div className={styles.empty}>
              {searchTerm ? 'No se encontró ningún propietario con ese criterio' : 'No tienes propietarios registrados'}
            </div>
          )}

          <div className={styles.clientList}>
            {filteredClients.map(client => {
              const isSelected = selectedClients.some(selected => selected.id === client.id);
              
              return (
                <div
                  key={client.id}
                  className={`${styles.clientCard} ${isSelected ? styles.selected : ''}`}
                  onClick={() => handleClientToggle(client)}
                >
                  <div className={styles.clientInfo}>
                    <div className={styles.clientName}>
                      {client.nombre} {client.apellido}
                    </div>
                    <div className={styles.clientDetails}>
                      <span className={styles.email}>📧 {client.mail}</span>
                      <span className={styles.phone}>📞 {client.telefono}</span>
                      {client.categoria && (
                        <span className={styles.category}>🏷️ {client.categoria}</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleClientToggle(client)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {selectedClients.length > 0 && (
            <div className={styles.selectedSummary}>
              <h4>Propietario Seleccionado:</h4>
              <div className={styles.selectedList}>
                {selectedClients.map(client => (
                  <span key={client.id} className={styles.selectedTag}>
                    {client.nombre} {client.apellido}
                    <button
                      type="button"
                      onClick={() => handleClientToggle(client)}
                      className={styles.removeButton}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSelector;